import { getTestBySlug } from "@/data/tests"
import { isAvailable } from "@/lib/availability"
import { isPathAllowedInCountry } from "@/lib/country-gates"
import type { Country } from "@/i18n/config"

/**
 * Whether a `/tests/<slug>` link must be kept out of this market's UI. The
 * desktop mega-menu, the mobile menu and the homepage services carousel all
 * hardcode the same service list, so they share this predicate rather than
 * each carrying a copy — they have to agree, and the one time the two menus
 * drifted they linked routes that 404.
 *
 * Three independent reasons a link disappears, and all three are needed
 * because each covers routes the others don't:
 *
 *  1. `navHiddenPaths` — CMS service state (`products.marketStatus[].
 *     hideFromNav`), passed by the menus only. Knows nothing about route
 *     gates.
 *  2. The `page:` gate registry — only the three literal add-on routes
 *     (`/tests/lpa`, `/tests/cortisol-blood-test`, `/tests/psa-blood-test`)
 *     have one.
 *  3. `test.availability` — the map the dynamic `/tests/<slug>` route actually
 *     404s on (`isAvailable` in tests/v/[code]/[slug]/page.tsx), and therefore
 *     the only gate covering every other test. Austria sells the MRI Advanced
 *     and Pro packages only and marks every standalone test `at-de: false`;
 *     without this clause /at/de keeps a full blood column pointing at 404s.
 *
 * CMS `marketStatus: hidden` covers much of the same ground as (3), but it
 * degrades open — `getServicesConfig()` returns an empty Map when the CMS is
 * unreachable and every consumer then renders ungated — so the repo-side
 * availability map is what guarantees a menu link can never out-live its route.
 *
 * `path` is the bare `/tests/<slug>`, without the `/{country}/{locale}` prefix.
 */
export function isServicePathHidden({
  path,
  country,
  locale,
  navHiddenPaths = [],
}: {
  path: string
  country: string
  locale: string
  /** CMS `hideFromNav` paths. Only the menus have these; the carousel gets its
   *  CMS state later, from `applyServicesConfig`. */
  navHiddenPaths?: string[]
}): boolean {
  if (navHiddenPaths.includes(path)) return true
  if (!isPathAllowedInCountry(path, country as Country)) return true
  const test = getTestBySlug(path.replace("/tests/", ""))
  // A path with no catalogue entry (e.g. a section link that isn't a test)
  // has no availability map to consult and is governed by the two checks above.
  return test ? !isAvailable(test.availability, country, locale) : false
}
